import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Building2, Truck, Check, Pencil, X, BadgeCheck, FileText, Settings2, NotebookPen, Coins } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
};
import ExportButtons from "@/components/ExportButtons";

const EMPTY = { name: "", type: "customer", email: "", phone: "", tax_number: "", balance: "" };

export default function Contacts({ fixedType }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [form, setForm] = useState({ ...EMPTY, type: fixedType || "customer" });
  const [editingId, setEditingId] = useState(null); // details dialog
  const [balEditId, setBalEditId] = useState(null);
  const [balVal, setBalVal] = useState("");
  const [colSum, setColSum] = useState({});
  const [colOpen, setColOpen] = useState(false);
  const [colContact, setColContact] = useState(null);
  const [colData, setColData] = useState(null);

  const openCollections = async (c) => {
    setColContact(c);
    setColData(null);
    setColOpen(true);
    try {
      setColData(await api.contactCollections(c.id));
    } catch {
      toast.error("Tahsilatlar yüklenemedi");
    }
  };

  const isFixed = fixedType === "customer" || fixedType === "supplier";
  const titleTxt = fixedType === "customer" ? "Müşteriler" : fixedType === "supplier" ? "Tedarikçiler" : "Cari Hesaplar";
  const subTxt = fixedType === "customer" ? "Müşteri listesi — ekle, düzenle, sil" : fixedType === "supplier" ? "Tedarikçi listesi — ekle, düzenle, sil" : "Müşteri ve tedarikçi bakiyeleri";

  const patchLocal = (id, patch) => setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const confirmBal = async (c) => {
    try {
      const d = await api.confirmContact(c.id);
      patchLocal(c.id, { confirmed: d.confirmed });
      toast.success("Cari bakiyesi onaylandı");
    } catch {
      toast.error("Onaylama başarısız");
    }
  };

  const startEditBal = (c) => {
    setBalEditId(c.id);
    setBalVal(String(c.balance));
  };

  const saveBal = async (c) => {
    const v = parseFloat(balVal);
    if (Number.isNaN(v)) {
      toast.error("Geçerli bir tutar girin");
      return;
    }
    try {
      const d = await api.adjustContactBalance(c.id, v);
      patchLocal(c.id, { balance: d.balance, manual_balance: true, confirmed: true });
      setBalEditId(null);
      toast.success("Bakiye güncellendi");
    } catch {
      toast.error("Güncelleme başarısız");
    }
  };

  const statement = async (c) => {
    try {
      await api.downloadExport(`/export/contact-statement/${c.id}?format=pdf`, `ekstre_${c.name}.pdf`);
      toast.success("Cari ekstresi indirildi");
    } catch {
      toast.error("Ekstre oluşturulamadı");
    }
  };

  const load = async () => {
    const [cs, cols] = await Promise.all([api.contacts(), api.collectionsSummary().catch(() => ({}))]);
    setContacts(cs);
    setColSum(cols || {});
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY, type: fixedType || "customer" });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name || "", type: c.type || "customer", email: c.email || "", phone: c.phone || "", tax_number: c.tax_number || "", balance: String(c.balance ?? "") });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name) {
      toast.error("İsim zorunludur");
      return;
    }
    const payload = { name: form.name, type: form.type, email: form.email, phone: form.phone, tax_number: form.tax_number, balance: parseFloat(form.balance) || 0 };
    try {
      if (editingId) {
        const d = await api.updateContact(editingId, payload);
        patchLocal(editingId, d);
        toast.success("Cari güncellendi");
      } else {
        await api.createContact(payload);
        toast.success("Cari hesap eklendi");
        load();
      }
      setOpen(false);
      setEditingId(null);
      setForm({ ...EMPTY, type: fixedType || "customer" });
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  const remove = async (id) => {
    await api.deleteContact(id);
    patchLocal(id, {});
    setContacts((prev) => prev.filter((c) => c.id !== id));
    toast.success("Cari hesap silindi");
  };

  const filtered = contacts.filter((c) => {
    if (isFixed) return c.type === fixedType;
    if (tab === "all") return true;
    if (tab === "online") return c.segment === "online";
    return c.type === tab;
  });
  const totalRecv = filtered.filter((c) => c.balance > 0).reduce((s, c) => s + c.balance, 0);
  const totalPay = filtered.filter((c) => c.balance < 0).reduce((s, c) => s - c.balance, 0);

  const exportPath = fixedType ? `/export/contacts` : "/export/contacts";

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title={titleTxt}
        subtitle={subTxt}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <ExportButtons path={exportPath} label="Cari listesi" testidPrefix="contacts-export" />
            <Button data-testid="add-contact-button" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4" /> <span className="ml-1.5">{fixedType === "supplier" ? "Yeni Tedarikçi" : fixedType === "customer" ? "Yeni Müşteri" : "Yeni Cari"}</span>
            </Button>
          </div>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-display">{editingId ? "Cari Düzenle" : "Yeni Cari Hesap"}</DialogTitle><DialogDescription>Cari bilgilerini girin veya güncelleyin.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            {!isFixed && (
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="contact-type-customer" onClick={() => setForm({ ...form, type: "customer" })} className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${form.type === "customer" ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}>Müşteri</button>
                <button data-testid="contact-type-supplier" onClick={() => setForm({ ...form, type: "supplier" })} className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${form.type === "supplier" ? "border-blue-500 bg-blue-500/15 text-blue-400" : "border-border text-muted-foreground"}`}>Tedarikçi</button>
              </div>
            )}
            <div><Label>Ünvan / İsim</Label><Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>E-posta</Label><Input data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Telefon</Label><Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Vergi No</Label><Input data-testid="contact-tax-input" value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label>Bakiye (lei) — pozitif: alacak, negatif: borç</Label>
              <Input data-testid="contact-balance-input" type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className="mt-1.5" placeholder="0,00" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="contact-submit-button" onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={colOpen} onOpenChange={setColOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Coins className="h-4 w-4 text-emerald-400" /> Banka Tahsilatları</DialogTitle>
            <DialogDescription>{colContact?.name} — banka dökümünden eşleşen tahsilatlar</DialogDescription>
          </DialogHeader>
          {!colData ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Yükleniyor…</div>
          ) : colData.items.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Tahsilat kaydı bulunamadı</div>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between rounded-lg bg-emerald-500/10 px-3 py-2 text-sm">
                <span className="text-muted-foreground">{colData.count} tahsilat</span>
                <span className="font-mono font-semibold text-emerald-300">Toplam {money(colData.total)}</span>
              </div>
              <div className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
                {colData.items.map((it, i) => (
                  <div key={i} data-testid="collection-row" className="flex items-center justify-between rounded-lg border border-border bg-secondary/20 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-mono text-sm">{fmtDate(it.date)}</div>
                      <div className="truncate text-[11px] text-muted-foreground">{it.counterparty}</div>
                    </div>
                    <div className="shrink-0 font-mono text-sm font-semibold text-emerald-300">{money(it.amount)}{it.currency !== "RON" ? ` ${it.currency}` : ""}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Toplam Alacak</div>
          <div className="mt-2 font-mono text-2xl font-semibold tabular text-emerald-400">{money(totalRecv)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Toplam Borç</div>
          <div className="mt-2 font-mono text-2xl font-semibold tabular text-rose-400">{money(totalPay)}</div>
        </div>
      </div>

      {!isFixed && (
        <div className="mb-4 flex gap-2">
          {[["all", "Tümü"], ["customer", "Müşteriler"], ["supplier", "Tedarikçiler"], ["online", "Online Satış"]].map(([k, l]) => (
            <button key={k} data-testid={`contact-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {filtered.length === 0 && <div className="py-12 text-center text-muted-foreground lg:col-span-2">Kayıt bulunamadı</div>}
        {filtered.map((c) => {
          const isCust = c.type === "customer";
          const Icon = isCust ? Building2 : Truck;
          const recv = c.balance >= 0;
          return (
            <div key={c.id} data-testid="contact-card" className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-0.5">
              <div className="flex items-center gap-3.5 min-w-0">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isCust ? "bg-primary/15 text-primary" : "bg-blue-500/15 text-blue-400"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{c.name}</span>
                    {c.segment === "online" && <span className="shrink-0 rounded-full bg-purple-500/15 px-2 py-0.5 text-[10px] font-semibold text-purple-300">ONLINE</span>}
                    {c.manual_balance ? (
                      <span data-testid="contact-status-manual" className="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold text-blue-400"><Pencil className="h-2.5 w-2.5" /> Düzeltildi</span>
                    ) : c.confirmed ? (
                      <span data-testid="contact-status-confirmed" className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-400"><BadgeCheck className="h-2.5 w-2.5" /> Onaylı</span>
                    ) : null}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{isCust ? "Müşteri" : "Tedarikçi"}{c.phone ? ` · ${c.phone}` : ""}</div>
                  {colSum[c.id] && (
                    <div data-testid="contact-last-collection" className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
                      <Coins className="h-3 w-3" />
                      Son tahsilat: {money(colSum[c.id].last_amount)}{colSum[c.id].last_currency !== "RON" ? ` ${colSum[c.id].last_currency}` : ""} · {fmtDate(colSum[c.id].last_date)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {balEditId === c.id ? (
                  <div className="flex items-center gap-1.5">
                    <Input data-testid="contact-balance-edit-input" type="number" value={balVal} autoFocus onChange={(e) => setBalVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveBal(c); if (e.key === "Escape") setBalEditId(null); }} className="h-8 w-28 text-right font-mono" />
                    <button data-testid="contact-save-balance" onClick={() => saveBal(c)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Check className="h-4 w-4" /></button>
                    <button data-testid="contact-cancel-balance" onClick={() => setBalEditId(null)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <>
                    <div className="text-right">
                      <div className={`font-mono text-base font-semibold tabular ${recv ? "text-emerald-400" : "text-rose-400"}`}>{money(Math.abs(c.balance))}</div>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{recv ? "Alacak" : "Borç"}</div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button data-testid="contact-notes-button" onClick={() => navigate(`/notlar?contact=${c.id}`)} title="Notlar" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-amber-500/15 hover:text-amber-300"><NotebookPen className="h-3.5 w-3.5" /></button>
                      {colSum[c.id] && (
                        <button data-testid="contact-collections-button" onClick={() => openCollections(c)} title="Banka Tahsilatları" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-emerald-500/15 hover:text-emerald-300"><Coins className="h-3.5 w-3.5" /></button>
                      )}
                      <button data-testid="contact-edit-details-button" onClick={() => openEdit(c)} title="Bilgileri Düzenle" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-primary/15 hover:text-primary"><Settings2 className="h-3.5 w-3.5" /></button>
                      <button data-testid="contact-statement-button" onClick={() => statement(c)} title="Cari Ekstresi (PDF)" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-teal-500/15 hover:text-teal-400"><FileText className="h-3.5 w-3.5" /></button>
                      <button data-testid="contact-confirm-button" onClick={() => confirmBal(c)} disabled={c.confirmed} title="Onayla" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-emerald-500/15 hover:text-emerald-400 disabled:opacity-30"><Check className="h-3.5 w-3.5" /></button>
                      <button data-testid="contact-edit-balance-button" onClick={() => startEditBal(c)} title="Bakiyeyi Düzelt" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-blue-500/15 hover:text-blue-400"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid="delete-contact-button" onClick={() => remove(c.id)} title="Sil" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-rose-500/15 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
